import React, { createContext, useContext, useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useUpdateCart, useAddToCart, buildUpdateCartRequest } from '@thd-olt-component-react/cart-common-data';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useConfigService } from '@thd-nucleus/experience-context';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { partsAndServicesDataModel } from './partsAndServicesDataModel';
import { usePartsAndServicesHook } from '../hooks/usePartsAndServicesHook';
import {
  updateCart,
  getAddOns,
  getAddOnsAndNewItemsArraysObj,
  postSelectionsToCart,
  findItemByConfigAttr,
  findItemByConfigAttrIgnoreCase
} from '../utils';
import {
  ADD_ON_CATEGORIES,
  ADD_ON_TYPES,
  INSTALL_ITEM_CONFIGURED,
  PAGE_TYPE
} from '../constants';

const PartsAndServicesContext = createContext({ closeDrawer: () => { } });
export const usePartsAndServices = () => useContext(PartsAndServicesContext);

export const PartsAndServicesProvider = ({
  addOnsSelections,
  applianceStoreId,
  children,
  currentPage,
  deliveryZip,
  disableHeader,
  disableNavigation,
  enableCheckoutEvent,
  fulfillmentSelections,
  isCheckoutFlow,
  itemIds,
  lineItemIds,
  enableInstallationFirst,
  typeOfSelection,
  addDirectlyToCart,
  pageNavigation,
  confirmationEnabled
}) => {
  const { updateItemsAddOns } = useUpdateCart();
  const [addToCart] = useAddToCart();
  const { isCustomerIdentified = false } = useThdCustomer() || {};

  const theme = useTheme(PartsAndServicesProvider);
  const { defaultDeclineCoverage, defaultDeclineInstall } = theme.props;

  const { data, error } = usePartsAndServicesHook({
    itemIds,
    storeZip: deliveryZip,
    isCustomerIdentified,
    skip: currentPage === PAGE_TYPE.CONFIRMATION
  });

  const isShowPSPPDownErrorMsgEnabled = useConfigService('fs-prop:isShowPSPPDownErrorMsgEnabled');
  const isEnableCompatibleAccessoriesPAS = useConfigService('enableCompatibleAccessoriesPAS');
  const isPSPPServiceDown = (isShowPSPPDownErrorMsgEnabled && error && !data?.getPartsServices) || false;

  // TODO: Once source data is corrected to have category:'required' for paid installs
  // return to using the below commented line instead of the .map
  // const products = data?.getPartsServices?.product;
  const products = data?.getPartsServices?.product?.map((product) => {
    return {
      ...product,
      addOns: product.addOns?.map((addOn) => {
        const { category, type } = addOn;
        if (category === 'applianceConfig' && type === 'Install') {
          return {
            ...addOn,
            category: 'required'
          };
        }
        return addOn;
      }) || []
    };
  });
  const productsMap = new Map();

  if (products) {
    lineItemIds.forEach((lineItemId, index) => {
      productsMap.set(lineItemId, products.find((product) => product.itemId === itemIds[index]));
    });
  }

  const showLoadingSkeleton = !products && !isPSPPServiceDown && currentPage !== PAGE_TYPE.CONFIRMATION;

  const [protectionSelections, setProtectionSelections] = useState({});
  const [installationSelections, setInstallationSelections] = useState({});
  const [haulAwaySelections, setHaulAwaySelections] = useState({});
  const [newProductSelections, setNewProductSelections] = useState({});
  const { protection = {}, installation = {}, haulAway = {} } = addOnsSelections;

  const initializeProtectionSelections = () => {
    const newProtectionSelections = {};
    lineItemIds.forEach((lineItemId) => {
      const lineItemProtection = protection[lineItemId] || '';
      const hasProtectionSelections = !!protectionSelections[lineItemId];
      const protectionItems = getAddOns({
        productsMap,
        lineItemId,
        addOnCategories: ADD_ON_CATEGORIES.PROTECTION
      })?.sort((first, second) => second?.termLength - first?.termLength);
      const hasProtectionItems = typeof protectionItems !== 'undefined';
      if (hasProtectionSelections) {
        // user has made selection in UI, has navigated away from protection, and has now navigated back
        // preserve the selection that the user made
        newProtectionSelections[lineItemId] = protectionSelections[lineItemId];
      } else if (!lineItemProtection && hasProtectionItems) {
        if (defaultDeclineCoverage) {
          newProtectionSelections[lineItemId] = {};
        } else {
          // If no user selections have been made, default to first option
          newProtectionSelections[lineItemId] = protectionItems[0];
        }
      } else if (hasProtectionItems) {
        // If user selection is indicated, then set initial state to user selection
        const selectedProtection = protectionItems?.filter(({ configAttr }) => configAttr === lineItemProtection)[0];
        newProtectionSelections[lineItemId] = selectedProtection;
      }
    });

    setProtectionSelections(newProtectionSelections);
  };

  const initializeHaulAwaySelections = () => {
    const newHaulAwaySelections = {};

    lineItemIds.forEach((lineItemId) => {
      const lineItemHaulAway = haulAway[lineItemId] || [];
      const hasHaulAwaySelections = !!haulAwaySelections[lineItemId]?.length;

      if (lineItemHaulAway?.length && !hasHaulAwaySelections) {
        const haulAwayItems = getAddOns({
          productsMap,
          lineItemId,
          addOnCategories: ADD_ON_CATEGORIES.HAUL_AWAY,
          addOnTypes: ADD_ON_TYPES.HAUL_AWAY
        });
        const selectedHaulAwayItems = lineItemHaulAway.map(
          (defaultConfigAttr) => findItemByConfigAttr(haulAwayItems, defaultConfigAttr)
        );

        newHaulAwaySelections[lineItemId] = [];
        selectedHaulAwayItems.forEach((haulAwayItem) => {
          newHaulAwaySelections[lineItemId].push(haulAwayItem);
        });

      } else if (hasHaulAwaySelections) {
        newHaulAwaySelections[lineItemId] = haulAwaySelections[lineItemId];
      }
    });

    setHaulAwaySelections(newHaulAwaySelections);
  };

  const initializeInstallationSelections = () => {
    const newInstallationSelections = {};

    lineItemIds.forEach((lineItemId) => {
      const lineItemInstallation = installation[lineItemId] || [];
      const hasInstallationSelections = !!installationSelections[lineItemId]?.length;
      const requiredItems = getAddOns({
        productsMap,
        lineItemId,
        addOnCategories: ADD_ON_CATEGORIES.INSTALLATION
      });
      const hasInstallationItems = typeof requiredItems !== 'undefined';
      const defaultSelectionsMade = lineItemInstallation && lineItemInstallation?.length > 0;

      const selectedInstallationItems = (lineItemInstallation || [])
        .filter((defaultConfigAttr) => defaultConfigAttr !== 'Configured')
        .map((defaultConfigAttr) => findItemByConfigAttrIgnoreCase(requiredItems, defaultConfigAttr))
        .filter(Boolean);

      if (hasInstallationSelections) {
        // User has existing selections and will have navigated between drawers
        newInstallationSelections[lineItemId] = installationSelections[lineItemId];
      } else if (lineItemInstallation.includes('Configured')) {
        // Edit, Decline Installation
        newInstallationSelections[lineItemId] = [INSTALL_ITEM_CONFIGURED];
        selectedInstallationItems.forEach((installationItem) => {
          newInstallationSelections[lineItemId].push(installationItem);
        });
      } else if (defaultSelectionsMade && selectedInstallationItems[0] !== undefined) {
        // Edit, Accept Installation
        newInstallationSelections[lineItemId] = [];
        selectedInstallationItems.forEach((installationItem) => {
          newInstallationSelections[lineItemId].push(installationItem);
        });
      } else if (hasInstallationItems) {
        // User coming from ATC/CTA, with no free install but required items exist,
        // should have self install selected yes by default
        if (defaultDeclineInstall) {
          newInstallationSelections[lineItemId] = [INSTALL_ITEM_CONFIGURED];
          selectedInstallationItems.forEach((installationItem) => {
            newInstallationSelections[lineItemId].push(installationItem);
          });
        } else {

          // should have install selected yes by default
          newInstallationSelections[lineItemId] = [...requiredItems];
        }
      }
    });
    setInstallationSelections(newInstallationSelections);
  };

  const toggleHaulAwaySelection = (lineItemId, haulAwayItem) => {

    const isAlreadySelected = haulAwaySelections?.[lineItemId]?.includes(haulAwayItem);
    const newHaulAwaySelections = {
      ...haulAwaySelections,
      [lineItemId]: [],
    };

    if (isAlreadySelected) {
      newHaulAwaySelections[lineItemId] = haulAwaySelections[lineItemId].filter((haulAwaySelection) => {
        return haulAwaySelection !== haulAwayItem;
      });
    } else {
      newHaulAwaySelections[lineItemId] = [...(haulAwaySelections?.[lineItemId] || []), haulAwayItem];
    }

    setHaulAwaySelections(newHaulAwaySelections);
  };

  const setProtectionSelectionsWrapper = (lineItemId, protectionItem) => {
    const newProtectionSelections = {
      ...protectionSelections,
      [lineItemId]: protectionItem,
    };

    setProtectionSelections(newProtectionSelections);
  };

  const setInstallationSelectionsWrapper = (lineItemId, ...installationItems) => {
    const newInstallationSelections = {
      ...installationSelections,
      [lineItemId]: installationItems,
    };
    setInstallationSelections(newInstallationSelections);
  };

  const toggleInstallationSelection = (lineItemId, installationItem) => {
    const isAlreadySelected = installationSelections?.[lineItemId]?.includes(installationItem);
    const newInstallationSelections = {
      ...installationSelections,
      [lineItemId]: [],
    };

    if (isAlreadySelected) {
      newInstallationSelections[lineItemId] = installationSelections[lineItemId].filter((installationSelection) => {
        return installationSelection !== installationItem;
      });
    } else {
      newInstallationSelections[lineItemId] = [...(installationSelections?.[lineItemId] || []), installationItem];
    }

    setInstallationSelections(newInstallationSelections);
  };

  const handleProductSelection = (compatibleAccesory) => {
    const { lineItemId, product: newProduct, isChecked } = compatibleAccesory;

    if (isChecked) {
      setNewProductSelections((prevSelections) => {
        const newSelections = { ...prevSelections };
        if (!newSelections[lineItemId]) {
          newSelections[lineItemId] = [];
        }
        newSelections[lineItemId].push(newProduct);
        return newSelections;
      });
    } else {
      setNewProductSelections((prevSelections) => {
        const newSelections = { ...prevSelections };
        if (newSelections[lineItemId]) {
          newSelections[lineItemId] = newSelections[lineItemId].filter(
            (product) => product.itemId !== newProduct.itemId
          );
        }
        return newSelections;
      });
    }
  };

  const handleAddProductsToCart = (atcProducts) => {
    return new Promise((resolve, reject) => {
      const atcProductsItems = atcProducts.map(({ fulfillmentLocation, fulfillmentMethod, itemId, qty }) => {
        return { fulfillmentLocation, fulfillmentMethod, itemId, quantity: qty || '1' };
      });
      const cartRequest = buildUpdateCartRequest(atcProductsItems);

      addToCart({ cartRequest }).then((res) => {
        if (res?.errors?.length) {
          return reject(new Error(`Cart update failed: ${JSON.stringify(res.errors)}`));
        }
        return resolve();
      }).catch((addToCartError) => {
        reject(new Error(`Add to cart error: ${addToCartError.message || addToCartError}`));
      });
    });
  };

  const { newItemsArray, newAddOnsArrays, newProductsArray } = getAddOnsAndNewItemsArraysObj({
    applianceStoreId,
    addOnsSelections,
    fulfillmentSelections,
    lineItemIds,
    itemIds,
    disableNavigation,
    currentPage,
    protectionSelections,
    installationSelections,
    haulAwaySelections,
    newProductSelections,
    products,
    deliveryZip,
    isCheckoutFlow,
    addDirectlyToCart
  });

  const initiateCartPost = (onSubmit) => {
    postSelectionsToCart({
      onSubmit,
      currentPage,
      updateItemsAddOns,
      enableCheckoutEvent,
      newItemsArray,
      newAddOnsArrays,
      handleAddProductsToCart,
      newProductsArray,
      typeOfSelection
    });
  };

  const hasInitiatedRef = useRef(false);

  useEffect(() => {
    if (!confirmationEnabled || hasInitiatedRef.current) {
      return;
    }

    if (addDirectlyToCart && !!products) {
      hasInitiatedRef.current = true;
      initiateCartPost((response) => {
        updateCart();
        pageNavigation.navigateToConfirmation(response);
      });
    } else if (!addDirectlyToCart && !showLoadingSkeleton) {
      hasInitiatedRef.current = true;
      pageNavigation.navigateToConfirmation();
    }
  }, [addDirectlyToCart, products, showLoadingSkeleton, confirmationEnabled]);

  return (
    <PartsAndServicesContext.Provider value={useMemo(() => ({
      disableHeader,
      fulfillmentSelections,
      haulAwaySelections,
      initializeProtectionSelections,
      initializeInstallationSelections,
      initializeHaulAwaySelections,
      installationSelections,
      initiateCartPost,
      itemIds,
      productsMap,
      protectionSelections,
      setProtectionSelections: setProtectionSelectionsWrapper,
      setInstallationSelections: setInstallationSelectionsWrapper,
      toggleHaulAwaySelection,
      toggleInstallationSelection,
      showLoadingSkeleton,
      isPSPPServiceDown,
      errors: error?.graphQLErrors?.map((graphQlError) => graphQlError.message) || [],
      enableInstallationFirst,
      newItemsArray,
      newAddOnsArrays,
      newProductsArray,
      isEnableCompatibleAccessoriesPAS,
      currentPage,
      currentExperience: typeof window !== 'undefined' ? window.experienceMetadata?.name : '',
      newProductSelections,
      handleProductSelection
    }), [
      disableHeader, fulfillmentSelections, haulAwaySelections, installationSelections, productsMap,
      protectionSelections, products, isPSPPServiceDown, error?.graphQLErrors, enableInstallationFirst,
      newItemsArray, newAddOnsArrays, currentPage, newProductSelections
    ])}
    >
      {children}
    </PartsAndServicesContext.Provider>
  );
};

PartsAndServicesProvider.propTypes = {
  applianceStoreId: PropTypes.string.isRequired,
  addOnsSelections: PropTypes.shape({
    haulAway: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    installation: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    protection: PropTypes.objectOf(PropTypes.string)
  }),
  fulfillmentSelections: PropTypes.shape(PropTypes.objectOf(PropTypes.string)).isRequired,
  currentPage: PropTypes.string.isRequired,
  typeOfSelection: PropTypes.string.isRequired,
  disableHeader: PropTypes.bool,
  disableNavigation: PropTypes.bool,
  enableCheckoutEvent: PropTypes.bool.isRequired,
  enableInstallationFirst: PropTypes.bool.isRequired,
  lineItemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  itemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  deliveryZip: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isCheckoutFlow: PropTypes.bool,
  addDirectlyToCart: PropTypes.bool,
  confirmationEnabled: PropTypes.bool,
  pageNavigation: PropTypes.shape({
    navigateToCart: PropTypes.func,
    navigateToProtection: PropTypes.func,
    navigateToServices: PropTypes.func,
    navigateToConfirmation: PropTypes.func
  }).isRequired
};

PartsAndServicesProvider.defaultProps = {
  addOnsSelections: {
    haulAway: {},
    installation: {},
    protection: {}
  },
  disableHeader: false,
  disableNavigation: false,
  isCheckoutFlow: false,
  addDirectlyToCart: false,
  confirmationEnabled: false
};

PartsAndServicesProvider.themeProps = {
  defaultDeclineCoverage: PropTypes.bool,
  defaultDeclineInstall: PropTypes.bool
};

PartsAndServicesProvider.defaultThemeProps = {
  defaultDeclineCoverage: false,
  defaultDeclineInstall: false
};

PartsAndServicesProvider.displayName = 'PartsAndServicesProvider';

PartsAndServicesProvider.dataModel = partsAndServicesDataModel;
